/**
 * Lógica de SLA (Acuerdos de Nivel de Servicio) para tickets.
 *
 * Las metas se diferencian por TIPO DE REGISTRO y por PRIORIDAD:
 *   SLA_POLICIES[type][priorityId] = { responseMins, resolutionMins }
 *
 * Un tipo puede no tener SLA (p. ej. los Cambios son planificados y no llevan
 * SLA reactivo): en ese caso su política es `null`.
 *
 * Prioridades: 1 Baja · 2 Media · 3 Alta · 4 Crítica. El tiempo es calendario
 * 24/7 (no se descuentan horas no laborables).
 */

const HOUR = 60
const DAY = 24 * 60

/**
 * Política de SLA por tipo de registro y prioridad, en minutos.
 * Ajusta estos valores según tus compromisos reales de servicio.
 */
export const SLA_POLICIES = {
  // Incidentes: reactivo, tiempos ajustados.
  incident: {
    4: { responseMins: 30, resolutionMins: 4 * HOUR },
    3: { responseMins: 1 * HOUR, resolutionMins: 8 * HOUR },
    2: { responseMins: 4 * HOUR, resolutionMins: 1 * DAY },
    1: { responseMins: 8 * HOUR, resolutionMins: 3 * DAY },
  },
  // Solicitudes: cumplimiento de servicio, resolución más holgada.
  request: {
    4: { responseMins: 1 * HOUR, resolutionMins: 8 * HOUR },
    3: { responseMins: 2 * HOUR, resolutionMins: 16 * HOUR },
    2: { responseMins: 8 * HOUR, resolutionMins: 2 * DAY },
    1: { responseMins: 1 * DAY, resolutionMins: 5 * DAY },
  },
  // Problemas: análisis de causa raíz, plazos largos.
  problem: {
    4: { responseMins: 4 * HOUR, resolutionMins: 5 * DAY },
    3: { responseMins: 8 * HOUR, resolutionMins: 10 * DAY },
    2: { responseMins: 1 * DAY, resolutionMins: 20 * DAY },
    1: { responseMins: 2 * DAY, resolutionMins: 30 * DAY },
  },
  // Cambios: planificados, sin SLA reactivo.
  change: null,

  // Tipos de desarrollo: se priorizan en backlog y se comprometen por entrega,
  // no por reloj, así que no llevan SLA reactivo.
  bug: null,
  feature: null,
  improvement: null,
  task: null,
}

/** Tipo/prioridad usados cuando el ticket no tiene un valor reconocido. */
const DEFAULT_TYPE = 'incident'
const DEFAULT_PRIORITY = 2

/** Umbral (fracción del tiempo total) a partir del cual se marca "en riesgo". */
const AT_RISK_FRACTION = 0.15

/**
 * Parsea una fecha asumiendo UTC cuando no trae zona horaria.
 * @param {string|Date|null} value
 * @returns {Date|null}
 */
const parseDate = (value) => {
  if (!value) return null
  if (value instanceof Date) return value
  const str = String(value)
  return new Date(str.endsWith('Z') || str.includes('+') ? str : str + 'Z')
}

/**
 * Políticas vigentes. Arrancan con los valores por defecto y se reemplazan con
 * la configuración guardada en `Tickets.slaPolicy` (Configuración → Tiempos de
 * SLA) al iniciar la app, vía `setSlaPolicies`. Es un registro de
 * configuración: todas las funciones de este módulo lo leen por defecto, pero
 * aceptan `policies` explícitas para tests o simulaciones.
 */
let activePolicies = SLA_POLICIES

/**
 * Reemplaza las políticas vigentes (o vuelve a las de por defecto con `null`).
 * @param {Object|null} policies - Mismo formato que `SLA_POLICIES`.
 */
export const setSlaPolicies = (policies) => {
  activePolicies = policies || SLA_POLICIES
}

/** Políticas vigentes (configuradas o por defecto). */
export const getSlaPolicies = () => activePolicies

/**
 * Convierte filas de `Tickets.slaPolicy` al formato de `SLA_POLICIES`.
 * Los tipos sin filas conservan su valor por defecto; un tipo cuyas filas
 * tienen todas los minutos nulos queda sin SLA (`null`); una celda nula
 * queda sin SLA sólo para esa prioridad.
 *
 * @param {Array<{type: string, priorityId: number, responseMins: number|null,
 *   resolutionMins: number|null}>} rows
 * @param {Object} [defaults=SLA_POLICIES]
 * @returns {Object}
 */
export const policiesFromRows = (rows = [], defaults = SLA_POLICIES) => {
  const result = { ...defaults }
  const byType = {}
  for (const row of rows) {
    if (!byType[row.type]) byType[row.type] = []
    byType[row.type].push(row)
  }

  for (const [type, typeRows] of Object.entries(byType)) {
    if (typeRows.every((r) => r.responseMins == null)) {
      result[type] = null
      continue
    }
    result[type] = Object.fromEntries(
      typeRows.map((r) => [
        r.priorityId,
        r.responseMins == null
          ? null
          : { responseMins: r.responseMins, resolutionMins: r.resolutionMins },
      ])
    )
  }
  return result
}

/**
 * Devuelve la política de SLA para un ticket según su tipo y prioridad.
 * @param {Object} ticket
 * @param {Object} [policies] - Políticas a usar (por defecto, las vigentes).
 * @returns {{responseMins: number, resolutionMins: number}|null} `null` si el
 *   tipo (o esa prioridad) no tiene SLA.
 */
export const getPolicy = (ticket, policies = activePolicies) => {
  // `null` es un valor válido (tipo sin SLA), así que hay que distinguirlo de
  // un tipo desconocido: con `??` los tipos sin SLA heredaban el de incidente.
  const conocido = Object.prototype.hasOwnProperty.call(policies, ticket?.type)
  const byType = conocido ? policies[ticket.type] : policies[DEFAULT_TYPE]
  if (!byType) return null
  // Una celda configurada explícitamente como "sin SLA" es `null`.
  if (byType[ticket?.priorityId] === null) return null
  return byType[ticket?.priorityId] || byType[DEFAULT_PRIORITY] || null
}

/**
 * Calcula la fecha objetivo de resolución (deadline) de un ticket.
 * Usa `dueAt` si está persistido; si no, lo deriva de `createdAt` + política.
 * @param {Object} ticket
 * @returns {Date|null} `null` si el ticket no tiene SLA ni `dueAt`.
 */
export const getResolutionTarget = (ticket) => {
  if (ticket?.dueAt) return parseDate(ticket.dueAt)
  const policy = getPolicy(ticket)
  const created = parseDate(ticket?.createdAt)
  if (!policy || !created) return null
  return new Date(created.getTime() + policy.resolutionMins * 60000)
}

/**
 * Formatea una duración (en milisegundos) de forma compacta: "2h 15m", "1d 3h".
 * @param {number} ms - Magnitud en milisegundos (se usa su valor absoluto).
 * @returns {string}
 */
export const formatDuration = (ms) => {
  const total = Math.max(0, Math.round(Math.abs(ms) / 60000)) // minutos
  const d = Math.floor(total / 1440)
  const h = Math.floor((total % 1440) / 60)
  const m = total % 60

  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

/**
 * Estado de un objetivo de SLA.
 * @typedef {Object} SlaTarget
 * @property {Date|null} target - Fecha límite.
 * @property {'met'|'breached'|'at_risk'|'on_track'|'pending'|'none'} status
 * @property {number} remainingMs - Tiempo restante (negativo si está vencido).
 * @property {boolean} done - Si el objetivo ya se cumplió/cerró.
 */

/**
 * Calcula el estado completo de SLA de un ticket.
 *
 * @param {Object} ticket - Ticket con type, priorityId, createdAt, dueAt,
 *   firstResponseAt, resolvedAt, completed.
 * @param {number} [now=Date.now()] - Marca de tiempo de referencia.
 * @returns {{
 *   hasSla: boolean,
 *   policy: Object|null,
 *   response: SlaTarget,
 *   resolution: SlaTarget,
 *   overall: 'met'|'breached'|'at_risk'|'on_track'|'pending'|'none'
 * }}
 */
export const getSlaInfo = (ticket, now = Date.now()) => {
  const policy = getPolicy(ticket)

  // Tipo sin SLA (p. ej. Cambios)
  if (!policy) {
    const none = { target: null, status: 'none', remainingMs: 0, done: false }
    return {
      hasSla: false,
      policy: null,
      response: none,
      resolution: none,
      overall: 'none',
    }
  }

  const created = parseDate(ticket?.createdAt)
  const resolved = Boolean(ticket?.completed)
  const resolvedAt = resolved ? parseDate(ticket?.resolvedAt) : null
  const firstResponseAt = parseDate(ticket?.firstResponseAt)

  const baseMs = created ? created.getTime() : now

  // ── Objetivo de primera respuesta ──────────────────────────────────────────
  const responseTarget = new Date(baseMs + policy.responseMins * 60000)
  let response
  if (firstResponseAt) {
    response = {
      target: responseTarget,
      status: firstResponseAt <= responseTarget ? 'met' : 'breached',
      remainingMs: responseTarget.getTime() - firstResponseAt.getTime(),
      done: true,
    }
  } else if (resolved) {
    response = {
      target: responseTarget,
      status: 'met',
      remainingMs: 0,
      done: true,
    }
  } else {
    const remainingMs = responseTarget.getTime() - now
    response = {
      target: responseTarget,
      status: remainingMs < 0 ? 'breached' : 'pending',
      remainingMs,
      done: false,
    }
  }

  // ── Objetivo de resolución ─────────────────────────────────────────────────
  const resolutionTarget =
    getResolutionTarget(ticket) ||
    new Date(baseMs + policy.resolutionMins * 60000)
  let resolution
  if (resolved && resolvedAt) {
    resolution = {
      target: resolutionTarget,
      status: resolvedAt <= resolutionTarget ? 'met' : 'breached',
      remainingMs: resolutionTarget.getTime() - resolvedAt.getTime(),
      done: true,
    }
  } else if (resolved) {
    resolution = {
      target: resolutionTarget,
      status: 'met',
      remainingMs: 0,
      done: true,
    }
  } else {
    const totalMs = resolutionTarget.getTime() - baseMs
    const remainingMs = resolutionTarget.getTime() - now
    let status = 'on_track'
    if (remainingMs < 0) status = 'breached'
    else if (totalMs > 0 && remainingMs < totalMs * AT_RISK_FRACTION)
      status = 'at_risk'
    resolution = { target: resolutionTarget, status, remainingMs, done: false }
  }

  // ── Severidad global (para el color del badge) ─────────────────────────────
  const severity = (s) =>
    ({ breached: 4, at_risk: 3, pending: 2, on_track: 1, met: 0, none: 0 })[
      s
    ] ?? 0
  const overall =
    severity(resolution.status) >= severity(response.status)
      ? resolution.status
      : response.status

  return { hasSla: true, policy, response, resolution, overall }
}

/** Metadatos visuales por estado de SLA. */
export const SLA_META = {
  met: { label: 'Cumplido', color: '#22c55e', icon: 'fa-circle-check' },
  on_track: { label: 'En tiempo', color: '#22c55e', icon: 'fa-circle-check' },
  at_risk: {
    label: 'En riesgo',
    color: '#f59e0b',
    icon: 'fa-triangle-exclamation',
  },
  pending: { label: 'Pendiente', color: '#64748b', icon: 'fa-clock' },
  breached: { label: 'Incumplido', color: '#ef4444', icon: 'fa-circle-xmark' },
  none: { label: 'Sin SLA', color: '#94a3b8', icon: 'fa-ban' },
}
